import React, { FC, useCallback, useEffect } from "react"
import { observer } from "mobx-react-lite"
import { Alert, Appearance, Dimensions, InteractionManager, Platform, TextStyle, View, ViewStyle } from "react-native"
import { Image, Screen, Text, updateSystemNavigatorBar } from "../../../components"
import { useStores } from "../../../models"
import { color } from "../../../theme"
import { AppStack, AppStackInitialSync, LoginStack, resetRoot } from "../../../navigators"
import { delay } from "../../../utils/delay"
import codePush from "react-native-code-push";
import { translate } from "../../../i18n"
import JalaAnalytic from "../../../utils/jala-analytic"
// import codePushCheck from "../../../utils/code-push"

const jalaAppsUdang = require("./jala-apps-udang.png")

const ROOT: ViewStyle = {
  flex: 1,
}

const LOGO_CONTAINER: TextStyle = {
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
}

const TITLE_TEXT: TextStyle = {
  marginTop: 17.84,
}

const HASHTAG_TEXT: TextStyle = {
  position: "absolute",
  bottom: 104,
  alignSelf: "center",
}

const VERSION_TEXT: TextStyle = {
  position: "absolute",
  bottom: 16,
  alignSelf: "center",
}

const IMAGE = {
  get width() {
    const w = Dimensions.get("window").width - 64;
    return w < 288 ? w : 288
  },
  get height() {
    return 64 * this.width / 288
  }
}

const hash = Platform.OS === 'web' ? new URLSearchParams(window.location.hash.replace('#', '')) : new URLSearchParams()

export const SplashScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function SplashScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  // Pull in one of our MST stores
  const rootStore = useStores()
  const {
    appStore,
    tokenStore,
    initialSyncStore
  } = rootStore

  const navigateTo = useCallback(async () => {
    if (tokenStore.demo && tokenStore.token) {
      await JalaAnalytic.logout();
      await rootStore.reset()
    }
    else if (!tokenStore.token && Platform.OS === 'web') {
      if (hash.has('access_token')) {
        tokenStore.setToken({
          token_type: hash.get('token_type'),
          expires_in: Number(hash.get('expires_in')),
          access_token: hash.get('access_token'),
          refresh_token: hash.get('refresh_token'),
        })
        window.location.hash = ''
      }
    }
    if (tokenStore.token) {
      await delay(1000)
      if (!initialSyncStore.synced)
        resetRoot(AppStackInitialSync);
      else {
        await updateSystemNavigatorBar({
          colorScheme,
          hideNavigationBar: false,
          navigationBarBackgroundColor: color.palette.white,
          backgroundColor: color.primary,
          statusBarStyle: colorScheme === "light" ? "light-content" : "dark-content",
          statusBarBackgroundColor: color.primary
        })
        resetRoot(AppStack)
      }
    }
    else {
      appStore.removeDatabase().catch()
      await delay(1000)
      resetRoot(LoginStack);
    }
  }, [appStore, colorScheme, initialSyncStore, rootStore, tokenStore])

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      appStore.init()
      if (codePush) {
        codePush.getUpdateMetadata().catch(() => null).then((localPackage) => {
          if (localPackage && localPackage.isFirstRun && translate("splashScreen.releaseMessage")) {
            if (appStore.lastReleaseMessage !== translate("splashScreen.releaseMessage")) {
              appStore.set('lastReleaseMessage', translate("splashScreen.releaseMessage"))
              Alert.alert(
                translate("splashScreen.releaseTitle", { version: `v${appStore.appVersion}` }),
                translate("splashScreen.releaseMessage"),
                [{ text: 'OK', onPress: () => navigateTo().catch() }]
              )
            } else {
              navigateTo().catch();
            }
          } else {
            navigateTo().catch();
            // codePushCheck().catch(() => null).then((remotePackage) => {
            //   if (!remotePackage || !remotePackage.isMandatory || remotePackage.failedInstall) navigateTo().catch();
            // });
          }
        })
      } else navigateTo().catch()
    })
  }, [appStore, navigateTo])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.primary : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideNavigationBar={true}
    >
      <View style={LOGO_CONTAINER}>
        <Image source={jalaAppsUdang} style={IMAGE} />
        <Text tx="splashScreen.splashScreenTitle" preset="body3" colorScheme="dark" style={TITLE_TEXT} />
      </View>
      <Text tx="splashScreen.hashtag" preset="headingBase" colorScheme="dark" style={HASHTAG_TEXT} />
      <Text text={`v${appStore.appVersion}`} preset="headingBase" colorScheme="dark" style={VERSION_TEXT} />
    </Screen>
  )
})
